import { jsonSafeClone } from './json-safe';

type BreadcrumbHook = (breadcrumb: unknown, hint?: unknown) => unknown;

type SentryClientOptions = {
  beforeBreadcrumb?: BreadcrumbHook;
};

type SentryLike = {
  getClient?: () => { getOptions: () => SentryClientOptions } | undefined;
};

let installed = false;
let retryScheduled = false;

/**
 * tauri-plugin-sentry's injected SDK sends every breadcrumb to Rust via
 * `invoke('plugin:sentry|breadcrumb')`. That path JSON.stringifies the
 * breadcrumb; a console/DOM payload with a cycle throws
 * "JSON.stringify cannot serialize cyclic structures" in the overlay.
 *
 * Run after the plugin inject (page JS). No-ops when Sentry is absent.
 * If the client is not ready on the first call, one microtask retry covers
 * the injected-SDK race; a second miss stays a no-op.
 */
export const installSentryBreadcrumbIpcGuard = (): void => {
  if (installed || typeof window === 'undefined') return;
  const sentry = (window as Window & { Sentry?: SentryLike }).Sentry;
  const options = sentry?.getClient?.()?.getOptions();
  if (!options) {
    if (!retryScheduled) {
      retryScheduled = true;
      queueMicrotask(installSentryBreadcrumbIpcGuard);
    }
    return;
  }

  const previous = options.beforeBreadcrumb;
  options.beforeBreadcrumb = (breadcrumb, hint) => {
    const safe = jsonSafeClone(breadcrumb) ?? { message: 'unserializable breadcrumb' };
    return previous ? previous(safe, hint) : safe;
  };
  installed = true;
};

export const resetSentryBreadcrumbIpcGuardForTests = (): void => {
  installed = false;
  retryScheduled = false;
};
